// 📄 components/Configuracion/ProgramaZFormModal.tsx
// Modal de alta Y edición de un Programa Z en Configuración > Desarrollo Z
// del proyecto (ver ConfiguracionProgramasZ.tsx) — mismo patrón que
// BadiZFormModal.tsx, con el agregado de las 2 relaciones N:N (BAdIs/
// Transacciones) vía CatalogoMultiSelect.tsx.
import { useEffect, useState } from "react";
import { X, Save, RefreshCw } from "lucide-react";
import { Timestamp } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext.tsx";
import {
  ProgramaZ, ProgramaZData, crearProgramaZ, actualizarProgramaZ
} from "../../services/programaZService.ts";
import { SistemaAmbiente, SISTEMAS_AMBIENTE } from "../../services/transaccionZService.ts";
import { SolucionBadi, SOLUCIONES_BADI, getBadis, BadiObjeto } from "../../services/catalogoBadisService.ts";
import { TransaccionSap, getTransacciones } from "../../services/catalogoTransaccionesService.ts";
import CatalogoMultiSelect from "../ui/CatalogoMultiSelect.tsx";

interface ProgramaZFormModalProps {
  projectId: string;
  // Presente = modo edición; ausente = alta.
  programaInicial?: ProgramaZ | null;
  onGuardado: () => void;
  onCancelar: () => void;
}

const timestampAInputDate = (ts: Timestamp | null | undefined): string => {
  if (!ts) return "";
  return ts.toDate().toISOString().slice(0, 10);
};

export default function ProgramaZFormModal({ projectId, programaInicial, onGuardado, onCancelar }: ProgramaZFormModalProps) {
  const { user } = useAuth();
  const modoEdicion = !!programaInicial;

  const [nombre, setNombre] = useState(programaInicial?.nombre || "");
  const [nombreTecnico, setNombreTecnico] = useState(programaInicial?.nombreTecnico || "");
  const [solucion, setSolucion] = useState<SolucionBadi | "">(programaInicial?.solucion || "");
  const [rolPermiso, setRolPermiso] = useState(programaInicial?.rolPermiso || "");
  const [sistemasDisponibles, setSistemasDisponibles] = useState<SistemaAmbiente[]>(programaInicial?.sistemasDisponibles || []);
  const [fechaImplementacion, setFechaImplementacion] = useState(timestampAInputDate(programaInicial?.fechaImplementacion));
  const [descripcion, setDescripcion] = useState(programaInicial?.descripcion || "");
  const [responsable, setResponsable] = useState(programaInicial?.responsable || "");
  const [notas, setNotas] = useState(programaInicial?.notas || "");
  const [badiRefIds, setBadiRefIds] = useState<string[]>(programaInicial?.badiRefIds || []);
  const [transaccionRefIds, setTransaccionRefIds] = useState<string[]>(programaInicial?.transaccionRefIds || []);

  const [badisOpciones, setBadisOpciones] = useState<{ id: string; label: string }[]>([]);
  const [transaccionesOpciones, setTransaccionesOpciones] = useState<{ id: string; label: string }[]>([]);

  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getBadis()
      .then((lista: BadiObjeto[]) => setBadisOpciones(lista.map((b) => ({ id: b.id, label: b.nombre }))))
      .catch((err) => console.error('Error cargando el catálogo de BAdIs para relaciones:', err));
    getTransacciones()
      .then((lista: TransaccionSap[]) => setTransaccionesOpciones(lista.map((t) => ({ id: t.id, label: `${t.transaccion} — ${t.nombre}` }))))
      .catch((err) => console.error('Error cargando el catálogo de Transacciones para relaciones:', err));
  }, []);

  const toggleSistema = (s: SistemaAmbiente) => {
    setSistemasDisponibles((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const handleGuardar = async () => {
    setError(null);
    if (!nombre.trim()) { setError("El nombre es obligatorio."); return; }
    if (!solucion) { setError("Selecciona una solución."); return; }

    const payload: ProgramaZData = {
      nombre: nombre.trim(),
      nombreTecnico: nombreTecnico.trim(),
      descripcion: descripcion.trim(),
      solucion,
      badiRefIds,
      transaccionRefIds,
      responsable: responsable.trim(),
      sistemasDisponibles,
      fechaImplementacion: fechaImplementacion ? Timestamp.fromDate(new Date(`${fechaImplementacion}T00:00:00`)) : null,
      rolPermiso: rolPermiso.trim(),
      notas: notas.trim()
    };

    setGuardando(true);
    try {
      if (modoEdicion) {
        await actualizarProgramaZ(projectId, programaInicial!.id, payload);
      } else {
        await crearProgramaZ(projectId, payload, user?.email || user?.uid || "");
      }
      onGuardado();
    } catch (err: any) {
      setError(err.message || "Error al guardar el Programa.");
    } finally {
      setGuardando(false);
    }
  };

  const intentarCerrar = () => { if (!guardando) onCancelar(); };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">{modoEdicion ? "Editar Programa Z" : "Nuevo Programa Z"}</h3>
          <button onClick={intentarCerrar} disabled={guardando} className="text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div>
            <label className={labelClass}>Nombre*</label>
            <input type="text" className={inputClass} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="ej. ZREP_ARBCIG_RECON - Reconciliación" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Nombre técnico</label>
              <input type="text" className={`${inputClass} font-mono`} value={nombreTecnico} onChange={(e) => setNombreTecnico(e.target.value)} placeholder="ej. ZREP_ARBCIG_RECON" />
            </div>
            <div>
              <label className={labelClass}>Solución*</label>
              <select className={inputClass} value={solucion} onChange={(e) => setSolucion(e.target.value as SolucionBadi)}>
                <option value="">Selecciona una solución</option>
                {SOLUCIONES_BADI.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Rol / Permiso SAP</label>
              <input type="text" className={`${inputClass} font-mono`} value={rolPermiso} onChange={(e) => setRolPermiso(e.target.value)} placeholder="ej. ZARBA_ROLE_COMPRAS" />
            </div>
            <div>
              <label className={labelClass}>Fecha de implementación</label>
              <input type="date" className={inputClass} value={fechaImplementacion} onChange={(e) => setFechaImplementacion(e.target.value)} />
            </div>
          </div>

          <div>
            <label className={labelClass}>Sistemas disponibles</label>
            <div className="flex items-center gap-4 pt-1">
              {SISTEMAS_AMBIENTE.map((s) => (
                <label key={s} className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
                  <input type="checkbox" className="accent-blue-500 w-3.5 h-3.5" checked={sistemasDisponibles.includes(s)} onChange={() => toggleSistema(s)} />
                  {s}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className={labelClass}>Descripción</label>
            <textarea className={`${inputClass} resize-y`} rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Para qué se usa este programa..." />
          </div>

          <div className="pt-2 border-t border-white/10 space-y-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Relaciones</p>
            <CatalogoMultiSelect
              label="BAdIs / Objetos ABAP relacionados"
              opciones={badisOpciones}
              seleccionados={badiRefIds}
              onChange={setBadiRefIds}
              placeholder="Agregar BAdI/objeto relacionado..."
            />
            <CatalogoMultiSelect
              label="Transacciones relacionadas"
              opciones={transaccionesOpciones}
              seleccionados={transaccionRefIds}
              onChange={setTransaccionRefIds}
              placeholder="Agregar transacción relacionada..."
            />
          </div>

          <div>
            <label className={labelClass}>Responsable</label>
            <input type="text" className={inputClass} value={responsable} onChange={(e) => setResponsable(e.target.value)} placeholder="Nombre del desarrollador/consultor responsable" />
          </div>

          <div>
            <label className={labelClass}>Notas técnicas</label>
            <textarea className={`${inputClass} resize-y`} rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Observaciones técnicas adicionales..." />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/10 flex-shrink-0">
          <button type="button" onClick={intentarCerrar} disabled={guardando} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleGuardar}
            disabled={guardando}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
          >
            {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {guardando ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
